import logger from "../utils/logger";
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authService, isAuthStorageKey } from '../utils/auth';
import { claimRing } from '../utils/callCoordinator';
import { createEventDeduper, domainEventId } from '../utils/commBadgeEvents';
import { toast } from 'sonner';
import api, { WS_BASE, getWsTicket } from '../utils/api';
import workspaceSocket from '../utils/workspaceSocket';
import { logWsDiagnostic } from '../utils/blankPageDiagnostics';
import { subscribeAuthSync, AUTH_SYNC_EVENTS } from '../utils/authSyncChannel';
import NotificationToast from '../components/NotificationToast';
import { resolveNotificationTarget } from '../hooks/useNotificationNavigation';

const FinanceNotificationContext = createContext(null);

export const FinanceNotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [financeNotifications, setFinanceNotifications] = useState([]);
  const [financeUnreadCount, setFinanceUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [notifError, setNotifError] = useState(null);

  const authErrorRef = useRef(false);
  const pollIntervalRef = useRef(null);
  const unsubWsRef = useRef(null);
  // Tracks notification IDs already shown as toasts to prevent duplicate toasts
  // from races between the WS event and the subsequent fetchNotifications() call.
  const seenToastIdsRef = useRef(new Set());
  // Approval / revenue / RBAC events arrive both over this tab's socket and relayed from
  // other tabs; each must be applied once. Short TTL so a later identical event still runs.
  const domainEventDeduperRef = useRef(createEventDeduper(300, 15000));
  const inFlightWorkspaceRef = useRef(null);
  const inFlightFinanceRef = useRef(null);
  const workspaceReqSeqRef = useRef(0);

  // Subscribe to connection status of shared workspace socket
  useEffect(() => {
    const unsubStatus = workspaceSocket.subscribeStatus(setConnected);
    return () => {
      unsubStatus();
    };
  }, []);

  const updateAuthError = useCallback((val) => {
    setAuthError(val);
    authErrorRef.current = val;
    if (val) {
      // Clear everything on auth error
      if (unsubWsRef.current) {
        unsubWsRef.current();
        unsubWsRef.current = null;
      }
      workspaceSocket.close();
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      setConnected(false);
    }
  }, []);

  // 1. Authoritative Workspace Notifications Fetch (Independent & Non-blocking)
  const fetchWorkspaceNotifications = useCallback(async ({ force = false } = {}) => {
    if (authErrorRef.current) return;
    if (!force && inFlightWorkspaceRef.current) {
      return inFlightWorkspaceRef.current;
    }

    const reqSeq = ++workspaceReqSeqRef.current;
    setLoading(prev => !initialLoaded);
    setRefreshing(prev => initialLoaded);

    const promise = (async () => {
      try {
        const wsRes = await api.get('/api/workspace/notifications');
        if (reqSeq < workspaceReqSeqRef.current) return; // Discard stale response

        const rawWs = Array.isArray(wsRes.data) ? wsRes.data : (wsRes.data?.notifications || []);
        const wsList = rawWs.map(n => ({
          ...n,
          id: n.id || n.notification_id,
          notification_id: n.notification_id || n.id,
          source: 'workspace',
          type: n.event_type || n.type || 'workspace_notification',
          event_type: n.event_type || n.type || 'workspace_notification',
          title: n.title || 'Workspace Update',
          message: n.message || n.content || '',
          timestamp: n.timestamp || n.created_at || new Date().toISOString(),
          created_at: n.created_at || n.timestamp || new Date().toISOString(),
          read: n.read_status ?? n.read ?? false,
          read_status: n.read_status ?? n.read ?? false,
          link: n.action_url || n.link || '/workspace',
          action_url: n.action_url || n.link || '/workspace',
          severity: n.severity || 'info',
          object_type: n.object_type || '',
          object_id: n.object_id || '',
          metadata: n.metadata || {}
        })).sort(
          (a, b) => new Date(b.created_at || b.timestamp || 0) - new Date(a.created_at || a.timestamp || 0)
        );

        const wsUnread = wsList.filter(n => !n.read_status && !n.read).length;
        setNotifications(wsList);
        setUnreadCount(wsUnread);
        setNotifError(null);
        setInitialLoaded(true);
      } catch (error) {
        if (reqSeq < workspaceReqSeqRef.current) return;
        if (error.response?.status === 401 && !authService.hasRefreshToken()) {
          updateAuthError(true);
          return;
        }
        const msg = error.response?.data?.message || error.response?.data?.detail || error.message || "Failed to load notifications";
        logger.error('Failed to fetch workspace notifications:', msg);
        setNotifError(msg);
        setInitialLoaded(true);
      } finally {
        if (reqSeq === workspaceReqSeqRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
        inFlightWorkspaceRef.current = null;
      }
    })();

    inFlightWorkspaceRef.current = promise;
    return promise;
  }, [initialLoaded, updateAuthError]);

  // 2. IB Finance Notifications Fetch (Independent & Non-blocking for Workspace)
  const fetchFinanceNotifications = useCallback(async ({ force = false } = {}) => {
    if (authErrorRef.current) return;
    if (!force && inFlightFinanceRef.current) {
      return inFlightFinanceRef.current;
    }

    const promise = (async () => {
      try {
        const res = await api.get('/api/finance-events/notifications');
        const rawFin = Array.isArray(res.data) ? res.data : (res.data?.notifications || res.data?.data || []);
        const finList = rawFin.map(n => ({
          ...n,
          id: n.id || n.event_id,
          notification_id: n.id || n.event_id,
          source: 'finance',
          type: n.event_type || n.type || 'finance_notification',
          event_type: n.event_type || n.type || 'finance_notification',
          title: n.title || 'Finance Notification',
          message: n.message || n.content || '',
          timestamp: n.timestamp || n.created_at || new Date().toISOString(),
          created_at: n.created_at || n.timestamp || new Date().toISOString(),
          read: n.read ?? n.read_status ?? false,
          read_status: n.read_status ?? n.read ?? false,
          link: n.action_url || n.link || '/finance',
          action_url: n.action_url || n.link || '/finance',
          severity: n.severity || 'info'
        }));
        setFinanceNotifications(finList);
        setFinanceUnreadCount(finList.filter(n => !n.read_status && !n.read).length);
      } catch (error) {
        if (error.response?.status === 401 && !authService.hasRefreshToken()) {
          updateAuthError(true);
          return;
        }
        logger.error('Failed to fetch finance notifications:', error?.message || "Unknown error");
      } finally {
        inFlightFinanceRef.current = null;
      }
    })();

    inFlightFinanceRef.current = promise;
    return promise;
  }, [updateAuthError]);

  // Unified fetch orchestrator — workspace updates immediately without waiting for finance
  const fetchNotifications = useCallback(async (options = {}) => {
    return Promise.allSettled([
      fetchWorkspaceNotifications(options),
      fetchFinanceNotifications(options)
    ]);
  }, [fetchWorkspaceNotifications, fetchFinanceNotifications]);

  const markAsRead = useCallback(async (id) => {
    if (!id) return;
    setNotifications(prev => prev.map(n => {
      if ((n.id || n.notification_id) === id) {
        return { ...n, read: true, read_status: true };
      }
      return n;
    }));
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await api.post(`/api/workspace/notifications/${id}/read`);
      if (typeof BroadcastChannel !== 'undefined') {
        try {
          const bc = new BroadcastChannel('workspace_notifications_channel');
          bc.postMessage({ type: 'notification_read', id });
          bc.close();
        } catch {}
      }
    } catch (err) {
      logger.error('Failed to mark notification as read:', err?.message || 'Unknown error');
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true, read_status: true })));
    setUnreadCount(0);

    try {
      await api.post('/api/workspace/notifications/read-all');
      if (typeof BroadcastChannel !== 'undefined') {
        try {
          const bc = new BroadcastChannel('workspace_notifications_channel');
          bc.postMessage({ type: 'all_notifications_read' });
          bc.close();
        } catch {}
      }
    } catch (err) {
      logger.error('Failed to mark all notifications as read:', err?.message || 'Unknown error');
    }
  }, []);

  /**
   * Mark all unread global notifications related to a specific entity as READ
   * without deleting. Kept for backward compatibility.
   * Prefer consumeNotificationsByContext for entity-open events.
   *
   * @param {string} contextId - The entity ID stored in the notification's
   *   `object_id` or `context_id` field.
   */
  const markNotificationsAsReadByContextId = useCallback(async (contextId) => {
    if (!contextId) return;
    const toMark = [];
    setNotifications(prev => prev.map(n => {
      const nContextId = n.object_id || n.context_id || n.metadata?.chat_id || n.metadata?.context_id;
      if (nContextId === contextId && !n.read_status && !n.read) {
        toMark.push(n.id || n.notification_id);
        return { ...n, read: true, read_status: true };
      }
      return n;
    }));
    if (toMark.length > 0) {
      setUnreadCount(prev => Math.max(0, prev - toMark.length));
      toMark.forEach(id => {
        api.post(`/api/workspace/notifications/${id}/read`).catch(() => {});
      });
    }
  }, []);

  /**
   * Consume (dismiss + delete) ALL global notifications for an entity when
   * the user opens it directly, via URL, via sidebar, or via notification click.
   *
   * Matching: a notification belongs to the entity if ANY of:
   *   n.context_id === contextId
   *   n.object_id  === contextId
   *   n.metadata?.chat_id === contextId
   *   n.metadata?.context_id === contextId
   *
   * And optionally: n.object_type === objectType (when provided).
   *
   * Optimistic: removes instantly, restores on backend failure.
   * Does NOT touch Chat/Channel unread badge or read-receipt systems.
   *
   * @param {object} opts
   * @param {string} opts.contextId    - The entity ID (chat_id, channel_id, task_id…)
   * @param {string} [opts.objectType] - Optional type filter: "chat" | "channel" | "task" | "approval"
   */
  const consumeNotificationsByContext = useCallback(async ({ contextId, objectType } = {}) => {
    if (!contextId) return;

    // 1. Identify all local notifications that belong to this entity
    let consumed = [];
    let unreadDelta = 0;

    setNotifications(prev => {
      const remaining = [];
      for (const n of prev) {
        const nId =
          n.context_id ||
          n.object_id ||
          n.metadata?.chat_id ||
          n.metadata?.context_id ||
          null;
        const typeMatch =
          !objectType ||
          (n.object_type || '').toLowerCase() === objectType.toLowerCase();
        if (nId === contextId && typeMatch) {
          consumed.push(n);
          if (!n.read && !n.read_status) unreadDelta++;
        } else {
          remaining.push(n);
        }
      }
      return remaining;
    });

    // 2. Adjust unread count immediately
    if (unreadDelta > 0) {
      setUnreadCount(prev => Math.max(0, prev - unreadDelta));
    }

    if (consumed.length === 0) return; // nothing to persist

    // 3. Backend bulk delete — with restore on failure
    try {
      await api.post('/api/workspace/notifications/consume-by-context', {
        context_id: contextId,
        ...(objectType ? { object_type: objectType } : {})
      });
    } catch (err) {
      logger.warn('[consumeNotifications] Backend consume failed, restoring', err?.message);
      // Restore the removed notifications
      setNotifications(prev => {
        const existingIds = new Set(prev.map(n => n.id || n.notification_id));
        const toRestore = consumed.filter(n => !existingIds.has(n.id || n.notification_id));
        return [...toRestore, ...prev];
      });
      if (unreadDelta > 0) {
        setUnreadCount(prev => prev + unreadDelta);
      }
    }
  }, []);

  /**
   * Optimistic local-only removal used by the click-to-navigate handler.
   * The hook (useNotificationNavigation) handles the backend DELETE call.
   * On DELETE failure the hook calls restoreNotification.
   */
  const dismissNotification = useCallback((notifId) => {
    if (!notifId) return;
    setNotifications(prev => {
      const target = prev.find(n => (n.id || n.notification_id) === notifId);
      if (target && !target.read && !target.read_status) {
        setUnreadCount(c => Math.max(0, c - 1));
      }
      return prev.filter(n => (n.id || n.notification_id) !== notifId);
    });
  }, []);

  /**
   * Restore a notification that was optimistically removed but whose backend
   * DELETE failed. Re-inserts at the front of the list.
   */
  const restoreNotification = useCallback((notification) => {
    if (!notification) return;
    const notifId = notification.id || notification.notification_id;
    setNotifications(prev => {
      const exists = prev.some(n => (n.id || n.notification_id) === notifId);
      if (exists) return prev;
      // Restore unread count if needed
      if (!notification.read && !notification.read_status) {
        setUnreadCount(c => c + 1);
      }
      return [notification, ...prev];
    });
  }, []);

  const clearNotification = useCallback(async (idOrIdx) => {
    let targetId = null;
    if (typeof idOrIdx === 'number') {
      const target = notifications[idOrIdx];
      targetId = target?.id || target?.notification_id;
    } else {
      targetId = idOrIdx;
    }
    if (!targetId) return;

    setNotifications(prev => {
      const target = prev.find(n => (n.id || n.notification_id) === targetId);
      if (target && !target.read && !target.read_status) {
        setUnreadCount(c => Math.max(0, c - 1));
      }
      return prev.filter(n => (n.id || n.notification_id) !== targetId);
    });

    try {
      await api.delete(`/api/workspace/notifications/${targetId}`);
      if (typeof BroadcastChannel !== 'undefined') {
        try {
          const bc = new BroadcastChannel('workspace_notifications_channel');
          bc.postMessage({ type: 'notification_deleted', id: targetId });
          bc.close();
        } catch {}
      }
    } catch (err) {
      logger.error('Failed to delete notification:', err?.message || 'Unknown error');
    }
  }, [notifications]);

  // Cross-tab toast presentation. A notification reaches every tab — over its own socket or
  // relayed from another tab — and every tab COMPETES for it here, so the visible / focused
  // tab wins the claim and shows the toast, and no tab shows it twice. (Previously the claim
  // was only attempted by the first tab to receive it, which could be a hidden one.)
  const toastAttemptedRef = useRef(new Set());
  const presentToastOnce = useCallback((normalized) => {
    if (!normalized || !normalized.title) return;
    const notifId = normalized.id || normalized.notification_id;
    if (notifId) {
      if (toastAttemptedRef.current.has(notifId)) return;
      toastAttemptedRef.current.add(notifId);
    }
    const showToast = () => {
      toast.custom(
        (t) => (
          <NotificationToast
            notification={normalized}
            onClose={() => toast.dismiss(t)}
            onNavigate={() => {
              toast.dismiss(t);
              const entityId =
                normalized.context_id ||
                normalized.object_id ||
                normalized.metadata?.chat_id ||
                normalized.metadata?.context_id;
              if (entityId && consumeNotificationsByContext) {
                consumeNotificationsByContext({
                  contextId: entityId,
                  objectType: normalized.object_type || undefined,
                });
              }
              const target = resolveNotificationTarget(normalized);
              if (target) {
                if (target.startsWith("/")) {
                  window.history.pushState(null, "", target);
                  window.dispatchEvent(new PopStateEvent("popstate"));
                } else {
                  window.location.assign(target);
                }
              }
            }}
          />
        ),
        { id: notifId, duration: 5000, unstyled: true, className: "workspace-custom-toast" }
      );
    };
    if (!notifId) {
      showToast();
      return;
    }
    claimRing(`toast:${notifId}`, { ttlMs: 6000 }).then((owned) => {
      if (owned) showToast();
    });
  }, [consumeNotificationsByContext]);

  const handleIncomingNotification = useCallback((data) => {
    if (!data) return;
    if (
      data.type === 'workspace.notification.created' ||
      data.event === 'workspace.notification.created' ||
      data.event === 'notification_created'
    ) {
      const raw = data.notification || data.payload?.notification || data.data;
      if (raw) {
        const normalized = {
          ...raw,
          id: raw.id || raw.notification_id,
          notification_id: raw.notification_id || raw.id,
          source: 'workspace',
          type: raw.event_type || raw.type || 'workspace_notification',
          event_type: raw.event_type || raw.type || 'workspace_notification',
          title: raw.title || 'Workspace Update',
          message: raw.message || raw.content || '',
          timestamp: raw.created_at || raw.timestamp || new Date().toISOString(),
          created_at: raw.created_at || raw.timestamp || new Date().toISOString(),
          read: false,
          read_status: false,
          link: raw.action_url || raw.link || '/workspace',
          action_url: raw.action_url || raw.link || '/workspace',
          severity: raw.severity || 'info'
        };

        const notifId = normalized.id || normalized.notification_id;
        const isNew = notifId && !seenToastIdsRef.current.has(notifId);

        setNotifications(prev => {
          const exists = prev.some(n => (n.id || n.notification_id) === notifId);
          if (exists) return prev;
          return [normalized, ...prev];
        });

        if (isNew) {
          if (notifId) seenToastIdsRef.current.add(notifId);
          setUnreadCount(prev => prev + 1);

          if (typeof BroadcastChannel !== 'undefined') {
            try {
              const bc = new BroadcastChannel('workspace_notifications_channel');
              bc.postMessage({ type: 'workspace.notification.created', notification: normalized });
              bc.close();
            } catch {}
          }

        }
        // Toast: every tab competes (visible / focused tab first); exactly one shows it.
        presentToastOnce(normalized);
      }
      fetchNotifications();
    }
  }, [fetchNotifications, presentToastOnce]);

  const connectWebSocket = useCallback(() => {
    if (authErrorRef.current) return;
    const token = authService.getToken();
    if (!token) return;
    if (unsubWsRef.current) return;

    unsubWsRef.current = workspaceSocket.subscribe((data) => {
      try {
        // A domain event already applied in this tab (relayed from another tab before our
        // own socket delivered it) must not be dispatched or relayed a second time.
        if (
          (data?.type === 'approval_event' || data?.type === 'revenue_event' || data?.type === 'rbac_event') &&
          !domainEventDeduperRef.current.accept(domainEventId(data.type, data))
        ) {
          return;
        }
        // Realtime synchronization for Workspace Approvals
        if (data?.type === 'approval_event') {
          window.dispatchEvent(new CustomEvent('workspace-approval-updated', { detail: data }));
          if (typeof BroadcastChannel !== 'undefined') {
            try {
              const bc = new BroadcastChannel('workspace_notifications_channel');
              bc.postMessage({ type: 'approval_event', data });
              bc.close();
            } catch {}
          }
        }

        // Realtime synchronization for Revenue Domain Events
        if (data?.type === 'revenue_event') {
          window.dispatchEvent(new CustomEvent('revenue-domain-event', { detail: data }));
          if (data?.event === 'approval_updated') {
            window.dispatchEvent(new CustomEvent('workspace-approval-updated', { detail: data }));
          }
          if (typeof BroadcastChannel !== 'undefined') {
            try {
              const bc = new BroadcastChannel('workspace_notifications_channel');
              bc.postMessage({ type: 'revenue_event', data });
              bc.close();
            } catch {}
          }
        }

        // Realtime synchronization for RBAC Role & Permission Events
        if (data?.type === 'rbac_event') {
          window.dispatchEvent(new CustomEvent('synexos:rbac-event', { detail: data }));
          if (typeof BroadcastChannel !== 'undefined') {
            try {
              const bc = new BroadcastChannel('workspace_notifications_channel');
              bc.postMessage({ type: 'rbac_event', data });
              bc.close();
            } catch {}
          }
        }

        handleIncomingNotification(data);
      } catch (err) {
        logger.error("[Notification WS] Error processing message:", err);
      }
    });
  }, [handleIncomingNotification]);

  // BroadcastChannel cross-tab synchronization
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel('workspace_notifications_channel');
    bc.onmessage = (event) => {
      const relayedType = event.data?.type;
      if (
        (relayedType === 'approval_event' || relayedType === 'revenue_event' || relayedType === 'rbac_event') &&
        !domainEventDeduperRef.current.accept(domainEventId(relayedType, event.data.data))
      ) {
        // Already applied from this tab's own socket.
        return;
      }
      if (event.data?.type === 'approval_event') {
        window.dispatchEvent(new CustomEvent('workspace-approval-updated', { detail: event.data.data }));
      }
      if (event.data?.type === 'revenue_event') {
        window.dispatchEvent(new CustomEvent('revenue-domain-event', { detail: event.data.data }));
        if (event.data.data?.event === 'approval_updated') {
          window.dispatchEvent(new CustomEvent('workspace-approval-updated', { detail: event.data.data }));
        }
      }
      if (event.data?.type === 'rbac_event') {
        // Dispatch to local window without re-posting to BroadcastChannel (loop prevention)
        window.dispatchEvent(new CustomEvent('synexos:rbac-event', { detail: event.data.data }));
      }

      if (
        event.data?.type === 'workspace.notification.created' ||
        event.data?.type === 'notification_created' ||
        event.data?.type === 'notification_read' ||
        event.data?.type === 'all_notifications_read'
      ) {
        if (event.data?.notification) {
          const n = event.data.notification;
          const relayedId = n.id || n.notification_id;
          // Count a relayed notification once: skip it if this tab already received it over
          // its own socket, and mark it so the later socket delivery is not counted again.
          const alreadySeen = Boolean(relayedId) && seenToastIdsRef.current.has(relayedId);
          if (!alreadySeen) {
            if (relayedId) seenToastIdsRef.current.add(relayedId);
            setNotifications(prev => {
              const exists = prev.some(item => (item.id || item.notification_id) === relayedId);
              if (exists) return prev;
              return [n, ...prev];
            });
            setUnreadCount(prev => prev + 1);
          }
          // Compete for the toast too: a visible tab that only learned of the notification
          // through the relay must still be able to show it.
          presentToastOnce(n);
        }
        fetchNotifications();
      }
    };
    return () => {
      bc.close();
    };
  }, [fetchNotifications, presentToastOnce]);

  useEffect(() => {
    let isMounted = true;

    const initOrRefresh = () => {
      if (!isMounted || authErrorRef.current) return;
      const orgStatus = localStorage.getItem('org_status');
      const isAuthPage = typeof window !== 'undefined' &&
        (window.location.pathname.includes('/auth/waiting-for-activation') ||
         window.location.pathname.includes('/auth/login') ||
         window.location.pathname.includes('/auth/signup'));
      const isOrgActive = !orgStatus || orgStatus === 'active';
      const token = authService.getToken();

      if (token && isOrgActive && !isAuthPage) {
        fetchNotifications();
        connectWebSocket();
      }
    };

    // Initial check
    initOrRefresh();

    // Subscribe to auth lifecycle events (login, silent token refresh, logout)
    const unsubscribeAuthSync = subscribeAuthSync((event) => {
      if (!isMounted) return;
      if (
        event.type === AUTH_SYNC_EVENTS.AUTH_LOGIN ||
        event.type === AUTH_SYNC_EVENTS.AUTH_TOKEN_UPDATED
      ) {
        updateAuthError(false);
        initOrRefresh();
      } else if (event.type === AUTH_SYNC_EVENTS.AUTH_LOGOUT) {
        setNotifications([]);
        setUnreadCount(0);
        setFinanceNotifications([]);
        setFinanceUnreadCount(0);
        seenToastIdsRef.current.clear();
        toastAttemptedRef.current.clear();
        if (unsubWsRef.current) {
          unsubWsRef.current();
          unsubWsRef.current = null;
        }
        workspaceSocket.close();
      }
    });

    // Storage change (e.g. cross-tab login or org_status change)
    const handleStorageChange = (e) => {
      // Only auth / org-status writes are relevant; activity heartbeats, meeting leases and
      // app state used to trigger two notification API calls per write in every tab.
      if (e && !(isAuthStorageKey(e.key) || e.key === 'org_status')) return;
      initOrRefresh();
    };
    window.addEventListener('storage', handleStorageChange);

    // Visibility / tab focus change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        initOrRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', initOrRefresh);

    // Listen for notification events forwarded by other active WebSockets (IncomingCallContext, WorkspaceChats, etc.)
    const handleForwardedNotif = (event) => {
      if (event?.detail) {
        handleIncomingNotification(event.detail);
      }
    };
    window.addEventListener('workspace-notification-received', handleForwardedNotif);

    // Regular polling fallback every 45s
    pollIntervalRef.current = setInterval(() => {
      initOrRefresh();
    }, 45000);

    return () => {
      isMounted = false;
      unsubscribeAuthSync();
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', initOrRefresh);
      window.removeEventListener('workspace-notification-received', handleForwardedNotif);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (unsubWsRef.current) {
        unsubWsRef.current();
        unsubWsRef.current = null;
      }
    };
  }, [fetchNotifications, connectWebSocket, updateAuthError, handleIncomingNotification]);

  return (
    <FinanceNotificationContext.Provider
      value={{
        loading,
        initialLoaded,
        refreshing,
        error: notifError,
        notifications,
        unreadCount,
        financeNotifications,
        financeUnreadCount,
        connected,
        fetchNotifications,
        fetchWorkspaceNotifications,
        fetchFinanceNotifications,
        markAsRead,
        markAllAsRead,
        clearNotification,
        dismissNotification,
        restoreNotification,
        markNotificationsAsReadByContextId,
        consumeNotificationsByContext
      }}
    >
      {children}
    </FinanceNotificationContext.Provider>
  );
};

export const useFinanceNotifications = () => useContext(FinanceNotificationContext);
export default FinanceNotificationContext;
